import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileState, {FileStateAttributes} from 'filestate/FileState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import * as navigation from 'Navigation';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import WorksheetTabState from 'notebook/worksheets/workspace/WorksheetTabState';
import {asJQueryPromise} from 'util/Promises';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {WorksheetFileQuery} from './__generated/WorksheetFileQuery';

type WorksheetFileGraphQL = ExtractByTypename<WorksheetFileQuery['worksheet'], 'WorksheetNode'>;

type DocumentGraphQL =
  WorksheetFileGraphQL['worksheetStepGroups'][number]['worksheetSteps'][number]['document'];

export const WORKSHEET_FILE_QUERY: DocumentNode = gql`
  query WorksheetFileQuery($fileId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    worksheet(id: $fileId) {
      ... on WorksheetNode {
        worksheetStepGroups {
          worksheetSteps {
            document {
              id
            }
          }
        }
        ...BaseFolderItemFileGraphQL
      }
      ... on InaccessibleInfoNode {
        ...BaseFolderItemFileGraphQL
      }
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
`;

export class Worksheet extends ApolloFolderItemBase<WorksheetFileQuery, WorksheetFileGraphQL> {
  protected getQueryWrapper(): QueryWrapper<WorksheetFileQuery, WorksheetFileGraphQL> {
    return {query: WORKSHEET_FILE_QUERY};
  }

  protected queryVariables(): {fileId: string} {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: WorksheetFileQuery
  ): WorksheetFileGraphQL | InaccessibleInfoGraphQL {
    return data.worksheet;
  }

  protected getUpdatedQueryData(attrs: Partial<WorksheetFileGraphQL>): WorksheetFileQuery {
    return {
      worksheet: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  stepDocuments(): Array<DocumentGraphQL> {
    return this.folderItem().worksheetStepGroups.flatMap((group) =>
      group.worksheetSteps.map((step) => step.document)
    );
  }
}

interface WorksheetFileState extends FileState {
  model(): Worksheet;
}

class WorksheetFileState extends FileState {
  static Model = Worksheet;
  static fileType = ModelTypeConstants.WORKSHEET;

  worksheetTab!: WorksheetTabState;

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  model(): Worksheet {
    return this.get(this.fileType());
  }

  beforeActivate(): void {
    this.model().setBanner();
  }

  registerTitleAndURLSync(): void {
    if (!this.model()) {
      return;
    }
    this.listenTo(this.model(), 'change:name', () => {
      this.set('name', this.model().name());
      this.syncTitleAndURL();
    });
    this.listenTo(this.model(), 'change:editURL', () => {
      this.syncTitleAndURL();
    });
  }

  initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);
    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    this.worksheetTab = new WorksheetTabState({worksheetFileState: this});
    buffer.tabs().add(this.worksheetTab);
    buffer.tabs().at(0).set({active: true});
  }

  updateWorksheet(worksheet: Worksheet): void {
    if (this.model()) {
      this.stopListening(this.model());
      this.cleanUp();
    }
    this.set(this.fileType(), worksheet);
    this.set('name', this.model().name());
    this.registerTitleAndURLSync();
    this.syncTitleAndURL();
  }

  async setId(id: string): Promise<FileState> {
    const model = new Worksheet({id});
    this.set('id', id);
    this.worksheetTab.set('id', id);

    await model.fetch({data: {view: true}});
    /**
     * this.get('id') may have changed after model.fetch if there was another request to setId that came
     * before this one resolved. In this case, the model is out of date and we should not use it.
     * e.g: User selects a Worksheet from dropdown, then immediately selects another while the previous one is still rendering.
     */
    if (this.get('id') === id) {
      this.updateWorksheet(model);
    }
    return this;
  }

  static openFile(fileId: string, param?: {versionId?: number}): JQueryPromise<FileState> {
    const fileState = new WorksheetFileState({}, {parse: true});
    return asJQueryPromise(fileState.setId(fileId));
  }

  hasUnsavedChanges(): boolean {
    return this.model()
      .stepDocuments()
      .some((document) => DocumentStore.inStore(document.id) && DocumentStore.hasUnsavedChanges(document.id));
  }

  cleanUp(): void {
    this.model()
      .stepDocuments()
      .forEach((document) => {
        if (DocumentStore.inStore(document.id)) {
          DocumentActions.close(document.id);
        }
      });
  }

  archiveRecord(): ArchiveRecord | null {
    return this.model().archiveRecord();
  }

  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.model().setArchiveRecord(
      archiveRecord != null ? {__typename: 'ArchiveRecordNode', purpose: archiveRecord.purpose} : null
    );
  }

  permissions(): Permissions | null {
    return this.model().permissions();
  }

  setPermissions(permissions: Permissions): void {
    this.model().setPermissions(permissions);
  }
}

export default WorksheetFileState;
